/**
 * InsightVariableFieldSelector
 *
 * Per-variable picker over the ACCEPTED variables of ONE insight-variable
 * generation (strategy-intake-feasibility, Task 9 / AC-019–AC-021).
 *
 * ── Why a thin wrapper ──────────────────────────────────────────────────────
 * The tree, the grouping by route, the manual-exclusion bookkeeping and the
 * hydration are exactly the project-variable picker's behaviour, so the tree is
 * `VariableFieldSelector`. Only two things differ and both live here:
 *
 * 1. the SOURCE — accepted variables of `insightVariableGenerations/{id}/
 *    generatedVariables` (loaded through firebase-core's
 *    `InsightVariableGenerationService`, `accepted === true` filtered here:
 *    the service already returns the flag, so no new service method exists);
 * 2. the PATH PLANE — `@insightVariable[id]`, never `@variable[id]`. The two
 *    planes are deliberately separate (owner decision: two manual pickers, no
 *    automatic semantic de-dup), so the inner picker's `@variable[...]` paths
 *    are translated in both directions at this boundary.
 *
 * The follow-the-routes rule (AC-016/017/018) is a PROJECT-variable rule and is
 * deliberately NOT declared here: every accepted variable stays visible and
 * preselected regardless of the pinned routes, and the operator narrows the set
 * by hand.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';
import {
  InsightVariableGenerationService,
  type GeneratedVariableEntityData,
} from '@modules/firebase-core/services/InsightVariableGenerationService';
import type { FunnelVariable } from '@segmently/schema-models/v2';

import { VariableFieldSelector } from '../VariableFieldSelector';
import type { InsightVariableFieldSelectorProps } from './types';

/** Path tag of the insight-derived plane. */
export const INSIGHT_VARIABLE_PATH_PREFIX = '@insightVariable[';

/** `id` → `@insightVariable[id]`. */
export function insightVariablePath(variableId: string): string {
  return `${INSIGHT_VARIABLE_PATH_PREFIX}${variableId}]`;
}

/** `@insightVariable[id]` → `id` (null for anything else). */
export function insightVariablePathToId(path: string): string | null {
  return path.match(/^@insightVariable\[([^\]]+)\]/)?.[1] ?? null;
}

/**
 * Reads the FunnelVariable out of a generatedVariables document. The agent
 * writes it under `data.variable`; imported/legacy documents carry a flat
 * `variable` — the resolver accepts both, so the picker must too.
 */
function toFunnelVariable(entity: GeneratedVariableEntityData): FunnelVariable | null {
  const raw = (entity as unknown as { data?: { variable?: unknown }; variable?: unknown });
  const candidate = (raw.data?.variable ?? raw.variable) as Record<string, unknown> | undefined;
  if (!candidate || typeof candidate !== 'object') return null;
  const id = String(candidate.id ?? entity.id ?? '').trim();
  if (!id) return null;
  return { ...(candidate as object), id } as FunnelVariable;
}

export const InsightVariableFieldSelector: React.FC<InsightVariableFieldSelectorProps> = ({
  projectId,
  generationId,
  value,
  onChange,
  tasks,
  height = 260,
  selectAll = true,
  onHydratedDataChange,
  onAcceptedCountChange,
  emptyStateAction,
  emptyStateMessage,
  showSelectionSummary = true,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accepted, setAccepted] = useState<FunnelVariable[]>([]);

  const service = useMemo(
    () => new InsightVariableGenerationService(projectId),
    [projectId],
  );

  // Load the generation's ACCEPTED variables. `listVariables` already returns
  // the `accepted` flag, so the filter is client-side and no firebase-core
  // service method had to be added.
  useEffect(() => {
    if (!generationId) {
      setAccepted([]);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    // Drop the previous generation's variables immediately: rendering them
    // while the new ones load would show a tree that belongs to nothing.
    setAccepted([]);

    service.listVariables(generationId)
      .then((entities) => {
        if (cancelled) return;
        setAccepted(
          entities
            .filter((entity) => entity.accepted === true)
            .map(toFunnelVariable)
            .filter((variable): variable is FunnelVariable => Boolean(variable)),
        );
      })
      .catch((loadError) => {
        if (cancelled) return;
        setAccepted([]);
        setError((loadError as Error)?.message || 'Failed to load insight-derived variables');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [service, generationId]);

  useEffect(() => {
    onAcceptedCountChange?.(accepted.length);
  }, [accepted.length, onAcceptedCountChange]);

  /**
   * The selection belongs to exactly ONE generation. Switching generations —
   * or going back to «product variables only» — must not leave the previous
   * generation's ids in the package the dialog would submit, so anything the
   * freshly loaded accepted set does not contain is pruned. The inner picker
   * then sees an empty external value and preselects the new set.
   */
  useEffect(() => {
    if (loading) return;
    const allowed = new Set(accepted.map((variable) => variable.id));
    const pruned = value.filter((path) => {
      const id = insightVariablePathToId(path);
      return id !== null && allowed.has(id);
    });
    if (pruned.length !== value.length) onChange(pruned);
  }, [loading, accepted, value, onChange]);

  /** The inner picker's `Record<id, variable>` source. */
  const variablesById = useMemo<Record<string, FunnelVariable>>(() => {
    const out: Record<string, FunnelVariable> = {};
    for (const variable of accepted) out[variable.id] = variable;
    return out;
  }, [accepted]);

  /**
   * Insight plane → project plane. Memoized on the CONTENT, so the inner
   * picker's initialisation effect is not re-armed by array identity alone.
   */
  const innerValueKey = value.join(',');
  const innerValue = useMemo<string[]>(
    () => value
      .map(insightVariablePathToId)
      .filter((id): id is string => Boolean(id))
      .map((id) => `@variable[${id}]`),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [innerValueKey],
  );

  /** Project plane → insight plane. */
  const handleInnerChange = useCallback((paths: string[]) => {
    onChange(
      paths
        .map((path) => path.match(/^@variable\[([^\]]+)\]/)?.[1])
        .filter((id): id is string => Boolean(id))
        .map(insightVariablePath),
    );
  }, [onChange]);

  if (!generationId) {
    return (
      <Box sx={{ p: 2 }} data-testid="insightVariables-picker-empty">
        <Typography variant="body2" color="text.secondary">
          {emptyStateMessage
            ?? 'Choose an insight-derived variable generation to pick its accepted variables.'}
        </Typography>
        {emptyStateAction && (
          <Box sx={{ mt: 0.5 }}>{emptyStateAction}</Box>
        )}
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ p: 2 }} data-testid="insightVariables-picker-loading">
        <LinearProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Loading insight-derived variables…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }} data-testid="insightVariables-picker-error">
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  // AC-021: an analysis whose generation accepted nothing is a legitimate,
  // non-blocking state — say so and point at where acceptance happens.
  if (accepted.length === 0) {
    return (
      <Box sx={{ p: 2 }} data-testid="insightVariables-picker-empty">
        <Typography variant="body2" color="text.secondary">
          No accepted variables in this analysis — review them in Insights
        </Typography>
        {emptyStateAction && (
          <Box sx={{ mt: 0.5 }}>{emptyStateAction}</Box>
        )}
      </Box>
    );
  }

  /*
   * `height` is a CAP here, not a fixed box: a generation usually accepts a
   * handful of variables and a fixed 400px would leave the section mostly
   * empty, out of step with the compact section in the design mock.
   */
  return (
    <Box data-testid="insightVariables-picker-tree" sx={{ maxHeight: height, overflow: 'auto' }}>
      <VariableFieldSelector
        variables={variablesById}
        tasks={tasks}
        value={innerValue}
        onChange={handleInnerChange}
        height="auto"
        selectAll={selectAll}
        onHydratedDataChange={onHydratedDataChange}
        showSelectionSummary={showSelectionSummary}
      />
    </Box>
  );
};

export default InsightVariableFieldSelector;
